import { motion } from "framer-motion";
import { Network, Monitor, Workflow, Users, UserRound } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PageHero } from "../components/pt/PageHero";
import { ClosingCTA } from "../components/pt/ClosingCTA";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const MISSION_ICONS = [Network, Monitor, Workflow, Users];

const About = () => {
    const { t } = useLang();
    const p = t.aboutPage;
    useDocumentTitle("/nosotros");

    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow={p.eyebrow}
                title={p.title}
                lead={p.lead}
                testId="about-hero"
            />

            {/* Mission / what we do */}
            <section className="sec" data-testid="about-mission">
                <div className="wrap">
                    <h2 className="h2 mb-12 max-w-[22ch]">{p.mission.title}</h2>
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                        {p.mission.items.map((m, i) => {
                            const Icon = MISSION_ICONS[i] || Network;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="group rounded-xl border border-linea bg-white p-6"
                                    data-testid={`about-mission-${i}`}
                                >
                                    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg bg-niebla text-coral-profundo transition-colors group-hover:bg-coral-profundo group-hover:text-white">
                                        <Icon size={20} />
                                    </div>
                                    <h3 className="h3 mb-2">{m.h}</h3>
                                    <p className="text-[0.98rem] text-acero">
                                        {m.p}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Values — Trust. Delivered. */}
            <section className="sec mist" data-testid="about-values">
                <div className="wrap grid gap-14 lg:grid-cols-[1fr_1.3fr]">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div
                            className="mb-4 text-sm font-bold uppercase tracking-[0.14em] text-coral-profundo"
                        >
                            {p.values.title}
                        </div>
                        <h2 className="h2 max-w-[16ch]">{p.values.title}</h2>
                        <p className="lead mt-4">{p.values.lead}</p>
                    </motion.div>
                    <motion.ol
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="space-y-0"
                    >
                        {p.values.items.map((v, i) => (
                            <li
                                key={i}
                                className="grid grid-cols-[64px_1fr] gap-5 border-t border-linea py-6 first:border-t-0 first:pt-0"
                                data-testid={`about-value-${i}`}
                            >
                                <div
                                    className="text-3xl font-semibold text-coral-profundo tab-nums"
                                    style={{ fontStretch: "118%" }}
                                >
                                    {v.n}
                                </div>
                                <div>
                                    <h3 className="h3 mb-1">{v.h}</h3>
                                    <p className="text-[0.98rem] text-acero">
                                        {v.p}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </motion.ol>
                </div>
            </section>

            {/* Team */}
            <section className="sec" data-testid="about-team">
                <div className="wrap">
                    <div className="mb-10 max-w-2xl">
                        <h2 className="h2">{p.team.title}</h2>
                        <p className="lead">{p.team.lead}</p>
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {p.team.people.map((person, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.55, delay: i * 0.1 }}
                                className="group overflow-hidden rounded-2xl bg-niebla"
                                data-testid={`about-team-${i}`}
                            >
                                <div className="relative flex aspect-[4/5] items-end bg-tinta p-6">
                                    <div className="absolute left-0 right-0 top-0 flex h-full items-center justify-center">
                                        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-tinta-2 text-coral">
                                            <UserRound size={40} />
                                        </div>
                                    </div>
                                    <div className="relative z-10 text-white">
                                        <div className="pt-eyebrow text-coral">
                                            <span className="dot" />
                                            <span style={{ color: "var(--pt-coral)" }}>
                                                Pentomino
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <h3 className="h3 mb-1" style={{ fontSize: "1.15rem" }}>
                                        {person.name}
                                    </h3>
                                    <p className="text-sm text-acero">
                                        {person.role}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    <p className="mt-6 text-sm text-acero">{p.team.footnote}</p>
                </div>
            </section>

            {/* Legal data */}
            <section className="sec mist" data-testid="about-legal">
                <div className="wrap">
                    <h2 className="h2 mb-10 max-w-2xl">{p.legal.title}</h2>
                    <div className="overflow-hidden rounded-2xl border border-linea bg-white">
                        <dl className="divide-y divide-linea">
                            {p.legal.items.map(([k, v]) => (
                                <div
                                    key={k}
                                    className="grid gap-2 p-5 sm:grid-cols-[220px_1fr] sm:gap-10"
                                >
                                    <dt className="text-sm font-semibold uppercase tracking-wider text-acero">
                                        {k}
                                    </dt>
                                    <dd className="text-[1rem] text-tinta">
                                        {v}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </section>

            <ClosingCTA testId="about-closing" />
        </div>
    );
};

export default About;
